import api from './api';

// Fallback filter pills, used only if GET /api/categories fails.
//
// These MUST stay in step with what the API actually serves. The previous
// hardcoded list (Promotions, Culture, Wine & Dine, Exhibition, Networking,
// Evening, Local Events, Hiking, Adventure, Nature, Wellness) shared exactly
// one name with the real taxonomy, so ~11 of 13 pills could never match an
// event and the categories that DO exist were never offered. Discovery — the
// core loop of the app — silently returned "No events found".
export const fallbackCategories = [
  'All', 'Sports', 'Tech', 'Arts', 'Food', 'Outdoor', 'Business', 'Social', 'Education'
];

// Kept for backwards compatibility with any caller still importing it.
export const availableCategories = fallbackCategories;

/**
 * Fetch the real category taxonomy for the filter pills.
 * Always returns a list beginning with 'All'; falls back to the static list
 * rather than leaving the user with no filters at all.
 */
export const fetchCategoryNames = async () => {
  try {
    const response = await api.get('/api/categories');
    const body = response.data;
    const rows = (body && body.data) ? body.data : body;
    const names = (Array.isArray(rows) ? rows : [])
      .map((c) => c.name || c.Name)
      .filter(Boolean);
    return names.length > 0 ? ['All', ...names] : fallbackCategories;
  } catch (err) {
    return fallbackCategories;
  }
};

// Shared function to fetch events
export const fetchEvents = async (params = {}) => {
  try {
    const response = await api.get('/api/events', { params });

    let eventData = [];
    if (Array.isArray(response.data)) {
      eventData = response.data;
    } else if (response.data && typeof response.data === 'object') {
      eventData = response.data.events || response.data.data || response.data;
      eventData = Array.isArray(eventData) ? eventData : [];
    }

    // Normalize event data to ensure consistent structure
    const normalizedEvents = eventData.map(event => ({
      ID: event.ID || event.id,
      Title: event.Title || event.title || 'Untitled Event',
      Description: event.Description || event.description || '',
      StartTime: event.StartTime || event.start_time,
      City: event.City || event.city || '',
      State: event.State || event.state || '',
      CurrentAttendees: event.CurrentAttendees || event.current_attendees || 0,
      Category: (() => {
        const cat = event.Category || event.category || ['General'];
        if (Array.isArray(cat)) {
          return cat.map(c => typeof c === 'object' && c !== null ? (c.name || c.Name || 'General') : c);
        }
        return typeof cat === 'object' && cat !== null ? (cat.name || cat.Name || 'General') : cat;
      })(),
      creator: event.creator || event.CreatedBy || 'Unknown',
      ImageURL: event.ImageURL || event.image_url || null,
      isJoined: event.isJoined || false
    }));

    // Return exactly what the API gave us. An empty list means "no events",
    // not "substitute demo data" -- the old mock fallback fabricated events
    // whose cards linked to /events/1..6, which 404 and land the user on the
    // "Error Loading Event" screen. It also masked API outages entirely.
    return normalizedEvents;
  } catch (error) {
    // Let callers decide how to present the failure; they all render an error
    // state already. Swallowing this is what made outages look like empty data.
    throw error;
  }
};

// Function to filter events by category (supports multiple categories)
export const filterEventsByCategory = (events, selectedCategory) => {
  if (!selectedCategory || selectedCategory === 'All') {
    return events;
  }

  return events.filter(event => {
    const eventCategories = Array.isArray(event.Category)
      ? event.Category
      : [event.Category];

    return eventCategories.some(category => {
      // Handle both string and object categories
      const categoryName = typeof category === 'object' && category !== null
        ? (category.name || category.Name || '')
        : String(category || '');

      return categoryName.toLowerCase().includes(selectedCategory.toLowerCase());
    });
  });
};

// Function to filter events by multiple criteria
export const filterEvents = (events, filters = {}) => {
  let filteredEvents = [...events];

  // Filter by category
  if (filters.category && filters.category !== 'All') {
    filteredEvents = filterEventsByCategory(filteredEvents, filters.category);
  }

  // Filter by location
  if (filters.location) {
    const locationQuery = filters.location.toLowerCase();
    filteredEvents = filteredEvents.filter(event =>
      (event.City && event.City.toLowerCase().includes(locationQuery)) ||
      (event.State && event.State.toLowerCase().includes(locationQuery))
    );
  }

  // Filter by distance (placeholder for future implementation)
  if (filters.distance && filters.distance !== 'Any distance') {
    // Distance filtering would require geolocation data
    // For now, just return the events as-is
  }

  // Filter by time (placeholder for future implementation)
  if (filters.time && filters.time !== 'Any time') {
    const now = new Date();
    filteredEvents = filteredEvents.filter(event => {
      const eventDate = new Date(event.StartTime);

      switch (filters.time) {
        case 'Today':
          return eventDate.toDateString() === now.toDateString();
        case 'This week':
          const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
          return eventDate >= now && eventDate <= weekFromNow;
        case 'This month':
          return eventDate.getMonth() === now.getMonth() &&
                 eventDate.getFullYear() === now.getFullYear();
        default:
          return true;
      }
    });
  }

  return filteredEvents;
};

// Function to join an event
export const joinEvent = async (eventId) => {
  try {
    await api.post(`/api/events/${eventId}/register`);
    return true;
  } catch (error) {
    console.error('Failed to join event:', error);
    throw error;
  }
};

export default {
  fetchEvents,
  filterEventsByCategory,
  filterEvents,
  joinEvent,
  availableCategories
};